import { AfterViewInit, Directive, ElementRef, HostListener, inject, Input } from '@angular/core';
import { NgControl } from '@angular/forms';
import { DecimalPipe } from '@angular/common';

/**
 * DecimalDisplayDirective
 *
 * Purpose:
 * - Restricts user input to a valid decimal number
 * - Allows optional leading minus sign
 * - Allows a single decimal separator (.)
 * - Limits fraction part to 3 digits
 * - Formats the value for display on blur
 */
@Directive({
  selector: '[decimalDisplay]',
  providers: [DecimalPipe],
})
export class DecimalDisplayDirective implements AfterViewInit {
  /** DecimalPipe format: min 1 integer digit, 3–3 fraction digits */
  @Input() public decimalFormat = '1.3-3';
  /** Locale used by DecimalPipe. Default: 'en-US' */
  @Input() public decimalLocale = 'en-US';

  private readonly el = inject(ElementRef<HTMLInputElement>);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly ngControl = inject(NgControl, { self: true, optional: true });

  private lastValidValue = '';

  private get control() {
    return this.ngControl?.control;
  }

  private get maxFractionDigits(): number {
    // Format is: "{minInt}.{minFrac}-{maxFrac}" e.g. "1.3-3"
    const m = /^(\d+)\.(\d+)-(\d+)$/.exec((this.decimalFormat ?? '').trim());
    const max = m ? Number(m[3]) : NaN;

    // Fallback to 3 to match the existing directive behavior/comment
    return Number.isFinite(max) ? max : 3;
  }

  @HostListener('input')
  public onInput(): void {
    const input = this.el.nativeElement;

    const value = input.value;
    const caret = input.selectionStart ?? value.length;

    // If the new value is invalid, revert the last change
    if (!this.isAllowed(value)) {
      const revertValue = this.lastValidValue;
      input.value = revertValue;
      this.control?.setValue(revertValue, { emitEvent: false });

      const pos = Math.min(revertValue.length, Math.max(caret - 1, 0));

      // Restore caret position after DOM update
      Promise.resolve().then(() => {
        input.setSelectionRange(pos, pos);
      });
    } else {
      this.lastValidValue = value;
    }
  }

  @HostListener('paste', ['$event'])
  public onPaste(event: ClipboardEvent): void {
    const input = this.el.nativeElement;

    // Text from clipboard
    const pasteText = event.clipboardData?.getData('text') ?? '';

    // Paste start position (cursor or selection start)
    const start = input.selectionStart ?? input.value.length;

    // Paste end position (cursor or selection end)
    const end = input.selectionEnd ?? input.value.length;

    // Value as it would look after paste
    const nextValue = input.value.slice(0, start) + pasteText + input.value.slice(end);

    // Cancel paste if resulting value is invalid
    if (!this.isAllowed(nextValue)) {
      event.preventDefault();
    }
  }

  @HostListener('blur')
  public onBlur(): void {
    this.formatDisplay();
  }

  @HostListener('focus')
  public onFocus(): void {
    const controlValue = this.control?.value;

    if (controlValue === null || controlValue === undefined) {
      return;
    }

    // Show unformatted value for editing
    const rawValue = controlValue.toString();
    this.el.nativeElement.value = rawValue;
    this.lastValidValue = rawValue;
  }

  public ngAfterViewInit(): void {
    this.formatDisplay();
    this.lastValidValue = this.el.nativeElement.value;
  }

  private isAllowed(value: string): boolean {
    // Allow empty value (input cleared)
    if (value === '') {
      return true;
    }

    // Allow single "-" as an intermediate typing state
    if (value === '-') {
      return true;
    }

    // Regex:
    // "-"? + digits* + ("." + digits*)?
    const re = /^-?\d*(\.\d*)?$/;

    // Reject if pattern does not match
    if (!re.test(value)) {
      return false;
    }

    // Enforce maximum of 3 fraction digits
    const dotIdx = value.indexOf('.');
    if (dotIdx >= 0) {
      const fractionLength = value.length - dotIdx - 1;
      if (fractionLength > this.maxFractionDigits) {
        return false;
      }
    }

    return true;
  }

  private formatDisplay(): void {
    const value = this.control?.value;

    if (value === null || value === undefined || value === '') {
      return;
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return;
    }

    this.el.nativeElement.value =
      this.decimalPipe.transform(numericValue, this.decimalFormat, this.decimalLocale) ?? '';
  }
}
